import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings, Clock, CalendarClock, Download, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function TripSettingsPanel({ tripId }) {
  const [s, setS] = useState({ stop_short_min: 20, stop_long_min: 90, overnight_min: 360, journal_retention_days: 30, timezone: "America/Merida" });
  const [expiry, setExpiry] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/trips/${tripId}`).then((r) => {
      const t = r.data; const ns = t.notify_settings || {};
      setS({
        stop_short_min: ns.stop_short_min ?? 20, stop_long_min: ns.stop_long_min ?? 90,
        overnight_min: ns.overnight_min ?? 360, journal_retention_days: t.journal_retention_days ?? 30,
        timezone: t.timezone || "America/Merida",
      });
      setExpiry(t.journal_expires_at);
    }).catch(() => {});
  }, [tripId]);

  const save = async () => {
    setBusy(true);
    try {
      const retention = Math.max(7, Math.min(30, Number(s.journal_retention_days) || 30));
      const { data } = await api.patch(`/admin/trips/${tripId}/settings`, { ...s, journal_retention_days: retention });
      setS((cur) => ({ ...cur, journal_retention_days: data.journal_retention_days ?? retention, ...(data.notify_settings || {}) }));
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const extend = async () => {
    try { const { data } = await api.post(`/admin/trips/${tripId}/journal/extend`, { days: 30 }); setExpiry(data.journal_expires_at); toast.success("Journal access extended 30 days"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/admin/trips/${tripId}/journal/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "trip-keepsake.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const num = (k, label, hint) => (
    <label className="block">
      <span className="text-xs text-charcoal-500">{label}</span>
      <input data-testid={`set-${k}`} type="number" min="1" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={s[k]} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} />
      {hint && <span className="text-[11px] text-charcoal-400">{hint}</span>}
    </label>
  );

  return (
    <div className="space-y-4" data-testid="settings-panel">
      <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-3">
        <div className="font-medium text-charcoal-800 text-sm flex items-center gap-2"><Clock size={15} /> Stop-duration thresholds</div>
        <p className="text-xs text-charcoal-500">A stationary period isn't always a rest stop — it may be traffic, a border queue or fuel. When a stop isn't near a known city it's described neutrally and flagged for your confirmation.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {num("stop_short_min", "Short stop (min)", "e.g. a quick break")}
          {num("stop_long_min", "Longer stop (min)", "e.g. a meal")}
          {num("overnight_min", "Overnight (min)", "e.g. 360 = 6h")}
        </div>
        <label className="block">
          <span className="text-xs text-charcoal-500">Timezone</span>
          <input data-testid="set-timezone" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} />
        </label>
      </div>

      <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-3">
        <div className="font-medium text-charcoal-800 text-sm flex items-center gap-2"><CalendarClock size={15} /> Private journal access</div>
        <p className="text-xs text-charcoal-500">The countdown starts when the trip is <b>completed</b>, not at departure. Expiry turns off the portal view only — photos and records are never deleted by expiry.</p>
        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <label className="block">
            <span className="text-xs text-charcoal-500">Access window after completion (7–30 days)</span>
            <input data-testid="set-retention" type="number" min="7" max="30" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={s.journal_retention_days} onChange={(e) => setS({ ...s, journal_retention_days: Number(e.target.value) })} />
          </label>
          <div className="text-xs text-charcoal-500">
            {expiry ? <>Expires: <b>{new Date(expiry).toLocaleDateString()}</b></> : "Not completed yet — countdown hasn't started."}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="extend-btn" onClick={extend} className="border rounded-full px-4 py-1.5 text-sm">Extend 30 days</button>
          <button data-testid="keepsake-btn" onClick={downloadPdf} className="bg-sand-100 text-charcoal-700 rounded-full px-4 py-1.5 text-sm flex items-center gap-1"><Download size={14} /> Download keepsake PDF</button>
        </div>
      </div>

      <button data-testid="settings-save-btn" onClick={save} disabled={busy} className="bg-terracotta text-white rounded-full px-5 py-2 text-sm flex items-center gap-1 disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />} Save settings
      </button>
    </div>
  );
}
